import React from "react";
import { Box, Flex } from "theme-ui";

import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import { themevals } from "theme/themevals";

/**
 * Shared section wrapper for every tab on the Client Detail page.
 *
 * Renders:
 *   - A section header row: title (H5) + optional subtitle + optional
 *     right-aligned action (button, link, or chip)
 *   - A white rounded card container that holds the tab body
 *
 * Every tab (Contacts, Plans, RFQs, Quotes, Projects, Invoices, POs,
 * Notes) uses this so the page has one consistent visual language
 * rather than the mix of raw TableData tables and hand-styled cards
 * we had before.
 *
 * `bodyPadding=false` lets tables extend to the card's edges without
 * fighting internal padding; hand-styled bodies (Notes composer, empty
 * states) turn it on for breathing room.
 */
export function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Flex
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "14px",
        flexWrap: "wrap",
        gap: "10px",
      }}
    >
      <Flex sx={{ alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
        <H5
          fontFamily="Poppins"
          fontWeight={600}
          margin={0}
          padding={0}
          color={themevals.colors.black}
          fontSize={"15px"}
        >
          {title}
        </H5>
        {subtitle && (
          <Subtext
            fontSize={"12px"}
            fontFamily={"Poppins"}
            margin={0}
            color={themevals.colors.dkgrey}
          >
            {subtitle}
          </Subtext>
        )}
      </Flex>
      {action}
    </Flex>
  );
}

export function SectionCard({
  children,
  bodyPadding = false,
}: {
  children: React.ReactNode;
  bodyPadding?: boolean;
}) {
  return (
    <Box
      sx={{
        background: "white",
        border: "1px solid",
        borderColor: "inputOutline",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)",
        ...(bodyPadding ? { padding: "20px" } : {}),
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Composite: SectionHeader + SectionCard in a single wrapper. Use this
 * for the majority of tab bodies. Drop down to SectionHeader / SectionCard
 * separately when you need multiple sections in one tab (see ContactsTab).
 */
export default function TabSection({
  title,
  subtitle,
  action,
  children,
  bodyPadding = false,
}: {
  title: string;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  bodyPadding?: boolean;
}) {
  return (
    <Box>
      <SectionHeader title={title} subtitle={subtitle} action={action} />
      <SectionCard bodyPadding={bodyPadding}>{children}</SectionCard>
    </Box>
  );
}
